import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { activate, validate, LicenseStatus } from '../services/license';
import './LicenseGate.css';

// App-wide licence gate. On launch it validates the device's licence (online,
// with an offline grace fallback). While the licence is usable the app renders
// normally — with a slim banner during trial / grace / near-expiry. When the
// licence is missing or expired it shows the activation screen instead of the
// app, so an unlicensed terminal can't be used.
const LicenseGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<LicenseStatus | null>(null); // null = checking
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    validate()
      .then((s) => alive && setStatus(s))
      .catch(
        (e) =>
          alive &&
          setStatus({ phase: 'unactivated', ok: false, message: (e as Error).message })
      );
    return () => {
      alive = false;
    };
  }, []);

  const doActivate = async () => {
    setErr('');
    setBusy(true);
    try {
      const s = await activate(key);
      setStatus(s);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="lic-screen">
        <div className="lic-card">
          <div className="lic-logo">EP</div>
          <p className="lic-checking">Checking licence…</p>
        </div>
      </div>
    );
  }

  // Licence not usable → block the app behind the activation screen.
  if (!status.ok) {
    const expired = status.phase === 'expired' || status.phase === 'revoked';
    return (
      <div className="lic-screen">
        <div className="lic-card">
          <div className="lic-logo">EP</div>
          <h1 className="lic-title">{expired ? 'Licence required' : 'Activate EPOS'}</h1>
          <p className="lic-msg">{status.message}</p>

          <label className="lic-label">
            Licence Key
            <input
              className="lic-input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && doActivate()}
              placeholder="EPOS-XXXX-XXXX-XXXX"
              autoFocus
              spellCheck={false}
            />
          </label>

          {err && <p className="lic-err">{err}</p>}

          <button className="lic-btn" onClick={doActivate} disabled={busy}>
            {busy ? 'Activating…' : 'Activate'}
          </button>

          <p className="lic-foot">Enter the licence key supplied with your purchase.</p>
        </div>
      </div>
    );
  }

  // Usable — show a heads-up banner for trial / offline-grace / near-expiry.
  // Suppress it on the POS (Bill) screen so it doesn't clutter checkout.
  const onPosScreen = location.pathname === '/';
  const showBanner =
    !onPosScreen &&
    (status.phase === 'trial' ||
      status.phase === 'grace' ||
      (status.daysLeft != null && status.daysLeft <= 7));

  return (
    <>
      {showBanner && (
        <div className={`lic-banner ${status.phase === 'grace' ? 'warn' : ''}`}>
          {status.message}
        </div>
      )}
      {children}
    </>
  );
};

export default LicenseGate;
